<template>
  <div class="map-elem-info movie">
    <div class="col-icon">
      <img class="resource-icon start" :src="startIconPath">
      <img class="resource-icon end" :src="endIconPath">
    </div>
    <div class="col-info">
      <div class="row1 mb6">
        <div class="left-side" v-if="movieList">
          <div>
            <span class="ib lbl">場所:</span>
            <span class="ib val">
              {{movieList.startKp.roadNameDispShort}}
            </span>
            <span class="ib val">
              {{movieList.startKp.direction}}
            </span>
            <span class="ib val" v-if="movieList.startKp.placeName !== 'main_line'">
              {{movieList.startKp.placeName}}
            </span>
            <span class="ib lbl ml12">KP:</span>
            <span class="ib val">
              {{movieList.startKp.kp.toFixed(1)}}~{{movieList.endKp.kp.toFixed(1)}}
            </span>
          </div>
          <div>
            <template v-if="!isSearchTypeRoute">
              <span class="ib lbl">日時:</span>
              <span class="ib val">
                {{dtFormat(movieList.startTs, 'yyyy/mm/dd HH:MM:SS')}}
                ~
                {{dtFormat(movieList.endTs, 'HH:MM:SS')}}
              </span>
              <span class="ib val ml2" v-if="showStartTsFuzzy">
                ({{ startTsFuzzy }})
              </span>
            </template>
            <template v-else>
              <span class="ib lbl">再生時間:</span>
              <span class="ib val">
                {{dtFormat(movieList.endTs, 'HH:MM:SS')}}
              </span>
            </template>
          </div>
        </div>
        <div class="right-side mr8" v-if="movieList">
          <template v-if="!isSearchTypeRoute">
            <span class="ib lbl">車両番号:</span>
            <span class="ib val">{{movieList.carName}}</span>
          </template>
        </div>
      </div>
      <div class="row2">
        <div class="left-side">
          <span class="tag-badge mr4 mb4" :class="'tag-badge-' + e.tagType1" v-for="(e, i) in movieTags" :key="i">
            {{e.tagName}}
          </span>
        </div>
        <div class="buttons ta-right mr8">
          <button type="button" key="play" class="btn btn-sm btn-primary" @click="play">
            <i class="fas fa-play"></i>
            再生
          </button>
          <button
            type="button"
            key="editMovieTag"
            class="btn btn-sm btn-info ml4"
            @click="editMovieTag"
            :disabled="isSearchTypeRoute"
          >
            <i class="fas fa-tag"></i>
            タグ編集
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
} from 'vue';
import type { MapElemInfo } from 'src/models';
import type { GIMovieList } from 'src/models/geoItem';
import type { MovieTag } from 'src/models/movie';
import useUpdateTimer from 'src/composables/useUpdateTimer';
import { dtFormat, fuzzyTimeDisp } from 'src/lib/dateHelper';

interface Props {
  elem: MapElemInfo<GIMovieList>;
}

const props = defineProps<Props>();

const emit = defineEmits(['play-movie', 'edit-movie-tag']);

const movieList = computed<GIMovieList>(() => props.elem.data);
const now = ref<Date>(new Date());
useUpdateTimer(now);

const isSearchTypeRoute = computed<boolean>(() => {
  return movieList.value.searchType === 'route';
});
const showStartTsFuzzy = computed<boolean>(() => {
  const secDiff = (now.value.valueOf() - (movieList.value.startTs?.valueOf() ?? 0)) / 1000;
  return secDiff < 86400 * 31;
});
const startTsFuzzy = computed<string>(() => {
  return fuzzyTimeDisp(movieList.value.startTs, now.value);
});
const movieTags = computed<MovieTag[]>(() => {
  const movieTagMap = movieList.value.movies.reduce((acc: Record<string, MovieTag>, movie) => {
    movie.tags.forEach(tag => { acc[tag.id] = tag; }); return acc;
  }, {});
  return Object.values(movieTagMap);
});

const startIconPath = '/static/img/movie_start_pin.png';
const endIconPath = '/static/img/movie_end_pin.png';

const play = () => {
  emit('play-movie', { dataName: 'movie', data: movieList.value });
};
const editMovieTag = () => {
  emit('edit-movie-tag', { dataName: 'movie', data: movieList.value });
};
</script>

<style lang="scss" scoped>
.map-elem-info {
  .col-icon {
    margin-right: 4px;
    display: flex;
    flex-direction: column;
    .resource-icon {
      width: 42px;
      &.start {
        margin: -4px 0 0 0;
      }
      &.end {
        margin: -30px 0 0 0;
      }
    }
  }
  .col-info {
    .row1, .row2 {
      display: flex;
      justify-content: space-between;
    }
    .row1 {
      flex-wrap: wrap;
    }
    .lbl {
      margin-right: 4px;
    }
    .left-side {
      flex-grow: 1;
    }
    .buttons {
      width: 152px;
      min-width: 152px;
      align-self: flex-end;
    }
  }
}
</style>
